import { useEffect } from "react";
import { SitePartData } from "../types.ts";
import i18n from "i18next";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { useFormInput } from "src/components/common/dialogs/formInput/useFormInput.tsx";
import { FormAsyncSelectField } from "src/components/common/forms/fields/FormAsyncSelectField.tsx";
import { SitePartQueryApi } from "src/api/generated/erp/parts/sitePart/api/sitePartQueryApi.ts";
import { PartView } from "src/api/generated/erp/db/types/tables/partView.ts";
import { concatWithPipe } from "src/utils/strings.tsx";
import { integerRule, requireRule } from "src/components/common/forms/validation.ts";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField.tsx";
import { FormEnumSelectField } from "src/components/common/forms/fields/FormEnumSelectField.tsx";
import { getPartTypeLabels } from "src/api/generated/erp/db/types/enums/partType.ts";
import { getAcquisitionMethodLabels } from "src/api/generated/erp/db/types/enums/acquisitionMethod.ts";
import { FormAsyncUserSelectField } from "src/components/views/users/FormAsyncUserSelectField.tsx";
import { FormNumberField } from "src/components/common/forms/fields/FormNumberField.tsx";
import { getPartValuationMethodLabels } from "src/api/generated/erp/db/types/enums/partValuationMethod.ts";
import { FormCheckbox } from "src/components/common/forms/fields/FormCheckbox.tsx";
import { Divider } from "@mui/material";
import { ProductionLineView } from "src/api/generated/erp/db/types/tables/productionLineView.ts";
import { ErpUnit } from "src/api/generated/erp/db/types/tables/erpUnit.ts";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import { useAavoForm } from "src/components/common/forms/useAavoForm/useAavoForm.ts";
import { AavoFormLayout } from "src/components/common/forms/AavoFormLayout.tsx";
import { CatalogPart } from "src/api/generated/erp/db/types/tables/catalogPart.ts";
import { faArrowDownToLine } from "@fortawesome/pro-regular-svg-icons";
import { AavoCheckbox } from "src/components/common/inputFields/AavoCheckbox.tsx";
import { logError } from "src/errorHandling/errorLogging.ts";
import { FormTextField } from "../../../../../../common/forms/fields/FormTextField.tsx";

interface SitePartFormStepProps {
	catalogPart: Partial<CatalogPart>;
	createSitePart: boolean;
	onUpdateCreateSitePart: (createSitePart: boolean) => void;
	sitePart: Partial<SitePartData>;
	onUpdateSitePart: (updates: Partial<SitePartData>) => void;
	templateSitePartId: number | undefined;
	templateCatalogPartId: number | undefined;
	onSelectTemplate: (templateSitePartId: number) => void;
	erpUnitOptions: ErpUnit[];
	productionLineOptions: ProductionLineView[];
	onValidationChange?: (isValid: boolean) => void;
}

const productionFieldNames = [
	"capacityQuantity",
	"defaultProductionLineId",
	"estimatedProductionTimeHours",
] as const;

export const SitePartFormStep = ({
	catalogPart,
	createSitePart,
	onUpdateCreateSitePart,
	sitePart: currentSitePart,
	onUpdateSitePart,
	onSelectTemplate,
	erpUnitOptions,
	productionLineOptions,
	templateSitePartId,
	templateCatalogPartId,
	onValidationChange,
}: SitePartFormStepProps) => {
	const { userSites, defaultSiteId } = useGlobalInitData();
	const showFormDialog = useFormInput();

	const defaultValues = {
		siteId: defaultSiteId,
		responsiblePersonId: catalogPart.responsiblePersonId,
		warehouseTransferUnitId: catalogPart.partUnitId,
		warehouseTransferFactor: 1,
		standardCost: 0,
		estimatedLeadtime: 1,
		batchSize: 1,
		automaticRelease: false,
		automaticClose: false,
		aggregateOnPurchaseOrder: false,
		aggregateOnShopOrder: false,
		valuationMethod: "STANDARD_COST",
		salesRemark: "",
		acquisitionRemark: "",
		...currentSitePart,
	} as const;

	const { control, watch, reset, formState, trigger, clearErrors, setValue } = useAavoForm<SitePartData, void>({
		defaultValues: defaultValues,
		onSubmit: () => {},
	});

	const partType = watch("partType");
	const acquisitionMethod = watch("acquisitionMethod");
	const siteId = watch("siteId");

	// Update values on first render
	useEffect(() => {
		onUpdateSitePart(defaultValues);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const subscription = watch((newValues) => {
			onUpdateSitePart(newValues);
		});
		return () => subscription.unsubscribe();
	}, [watch, onUpdateSitePart]);

	useEffect(() => {
		if (partType !== "MANUFACTURE") {
			clearErrors([...productionFieldNames]);
		}
		trigger(["partType", ...productionFieldNames]).catch(logError);
	}, [clearErrors, partType, trigger]);

	useEffect(() => {
		// If createSitePart is false, always report as valid
		onValidationChange?.(createSitePart ? formState.isValid : true);
	}, [formState.isValid, onValidationChange, createSitePart]);

	useEffect(() => {
		trigger().catch(logError);
	}, [trigger]);

	const siteProductionLineOptions = productionLineOptions.filter((pl) => !siteId || pl.siteId === siteId);
	const showAggregateOnPurchaseOrder =
		partType === "PURCHASE" && (acquisitionMethod === "PURCHASE_TRANSIT" || acquisitionMethod === "PURCHASE_DIRECT");
	const showAggregateOnShopOrder = partType === "MANUFACTURE";

	return (
		<AavoFormLayout
			columns={2}
			layoutSx={{
				padding: 0,
			}}
		>
			<AavoCheckbox
				label={i18n.t("create_site_part")}
				checked={createSitePart}
				onChange={(checked) => onUpdateCreateSitePart(checked)}
				sx={{ gridColumn: "1/-1" }}
			/>
			{createSitePart && (
				<>
					<AsyncButton
						label={i18n.t("import_from_template")}
						icon={faArrowDownToLine}
						onClick={handleSelectTemplate}
					/>
					<FormSelectField
						control={control}
						name={"siteId"}
						label={i18n.t("site")}
						options={userSites}
						getOptionKey={(option) => option.siteId}
						getOptionLabel={(option) => option.siteName}
						rules={requireRule()}
						startNewGridRow
					/>
					<FormEnumSelectField
						control={control}
						name={"partType"}
						label={i18n.t("type")}
						options={getPartTypeLabels()}
						rules={requireRule()}
						onChange={(newPartType) => {
							if (newPartType !== "MANUFACTURE") {
								productionFieldNames.forEach((fieldName) => setValue(fieldName, null));
								clearErrors([...productionFieldNames]);
							} else if (watch("capacityQuantity") == null) {
								setValue("capacityQuantity", 1);
							}
						}}
					/>
					<FormEnumSelectField
						control={control}
						name={"acquisitionMethod"}
						label={i18n.t("acquisition_method")}
						options={getAcquisitionMethodLabels()}
						rules={requireRule()}
					/>
					<FormAsyncUserSelectField
						control={control}
						name={"responsiblePersonId"}
						label={i18n.t("responsible_person")}
						rules={requireRule()}
					/>
					<FormSelectField
						control={control}
						name={"warehouseTransferUnitId"}
						label={i18n.t("warehouse_transfer_unit")}
						options={erpUnitOptions}
						getOptionKey={(option) => option.unitId}
						getOptionLabel={(option) => option.unitName}
						rules={requireRule()}
					/>
					<FormNumberField
						control={control}
						name={"warehouseTransferFactor"}
						label={i18n.t("warehouse_transfer_factor")}
						rules={requireRule()}
					/>
					<FormNumberField
						control={control}
						name={"standardCost"}
						label={i18n.t("cost")}
						rules={requireRule()}
					/>
					<FormEnumSelectField
						options={getPartValuationMethodLabels()}
						control={control}
						name={"valuationMethod"}
						label={i18n.t("valuation_method")}
					/>
					<FormNumberField
						control={control}
						name={"estimatedLeadtime"}
						label={i18n.t("lead_time")}
						rules={integerRule()}
					/>
					<FormNumberField control={control} name={"batchSize"} label={i18n.t("batch_size")} type="integer" />
					<FormTextField control={control} name={"salesRemark"} label={i18n.t("sales_remark")} />
					<FormTextField control={control} name={"acquisitionRemark"} label={i18n.t("acquisition_remark")} />
					{partType === "MANUFACTURE" && (
						<>
							<Divider
								sx={{
									gridColumn: "1/-1",
									color: "primary.main",
								}}
							>
								{i18n.t("production")}
							</Divider>
							<FormNumberField
								control={control}
								name={"capacityQuantity"}
								label={i18n.t("capacity_quantity")}
								rules={requireRule()}
							/>
							<FormNumberField
								control={control}
								name={"estimatedProductionTimeHours"}
								label={i18n.t("production_time_hours")}
							/>
							<FormSelectField
								control={control}
								name={"defaultProductionLineId"}
								label={i18n.t("default_production_line")}
								options={siteProductionLineOptions}
								getOptionKey={(option: ProductionLineView) => option.productionLineId}
								getOptionLabel={(option) => option.productionLineName}
								rules={requireRule()}
							/>
							<FormCheckbox
								control={control}
								name={"automaticRelease"}
								label={i18n.t("automatic_release")}
							/>
							<FormCheckbox control={control} name={"automaticClose"} label={i18n.t("automatic_close")} />
						</>
					)}
				{showAggregateOnPurchaseOrder && (
					<FormCheckbox
						control={control}
						name={"aggregateOnPurchaseOrder"}
						label={i18n.t("aggregate_on_purchase_order")}
						helperText={i18n.t("aggregate_on_purchase_order_helper")}
						startNewGridRow
					/>
				)}
				{showAggregateOnShopOrder && (
					<FormCheckbox
						control={control}
						name={"aggregateOnShopOrder"}
						label={i18n.t("aggregate_on_shop_order")}
						helperText={i18n.t("aggregate_on_shop_order_helper")}
						startNewGridRow
					/>
				)}
				</>
			)}
		</AavoFormLayout>
	);

	async function handleSelectTemplate() {
		const defaultTemplatePart = await getDefaultTemplatePart();

		const formResult = await showFormDialog<{ siteId: number; sitePart: PartView }>({
			title: i18n.t("select_template_part"),
			size: "sm",
			defaultValues: {
				siteId: defaultSiteId,
				sitePart: defaultTemplatePart,
			},
			submitLabel: i18n.t("import"),
			submitOnEnter: true,
			content: ({ control, watch, setValue }) => (
				<>
					<FormSelectField
						control={control}
						name={"siteId"}
						label={i18n.t("site")}
						options={userSites}
						getOptionKey={(option) => option.siteId}
						getOptionLabel={(option) => option.siteName}
						rules={requireRule()}
						onChange={() => {
							setValue("sitePart", undefined);
						}}
					/>
					<FormAsyncSelectField
						control={control}
						name={"sitePart"}
						formValueType={"option"}
						label={i18n.t("template")}
						fetchOptions={async ({ searchQuery, currentSelection }) =>
							SitePartQueryApi.getPartSelectionOptions({
								searchQuery: searchQuery,
								currentPartId: currentSelection,
								siteId: watch("siteId"),
							})
						}
						getOptionKey={(option: PartView) => option.partId}
						getOptionLabel={(option) =>
							concatWithPipe(option.partNo, option.partDescription_1, option.partDescription_2)
						}
						rules={requireRule()}
						autoFocus
						openOnFocus={defaultTemplatePart == null}
					/>
				</>
			),
		});
		if (formResult === undefined) return;
		const templatePart = formResult.sitePart;

		reset({
			...templatePart,
			defaultProductionLineId: templatePart.defaultProductionLineId,
			capacityQuantity: templatePart.capacityQuantity ?? 1,
			estimatedProductionTimeHours: templatePart.estimatedProductionTimeHours ?? 0,
			estimatedLeadtime: templatePart.estimatedLeadtime ?? 1,
			batchSize: templatePart.batchSize ?? 1,
			salesRemark: templatePart.salesRemark ?? "",
			acquisitionRemark: templatePart.acquisitionRemark ?? "",
		});

		onSelectTemplate(templatePart.partId);
	}

	async function getDefaultTemplatePart(): Promise<PartView | undefined> {
		if (templateSitePartId != null) {
			return await SitePartQueryApi.getPart({ partId: templateSitePartId });
		}
		if (templateCatalogPartId != null) {
			return (
				(await SitePartQueryApi.getPartForCatalogPartOrNull({
					catalogPartId: templateCatalogPartId,
					siteId: defaultSiteId,
				})) ?? undefined
			);
		}
		return undefined;
	}
};
